package com.eladfinish.bible.contest.screens.mikud.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.book_name
import com.eladfinish.bible.contest.colorAccent
import com.eladfinish.bible.contest.screens.mikud.model.BookClickData
import com.eladfinish.bible.contest.screens.mikud.model.BookState
import com.eladfinish.bible.contest.screens.mikud.model.BookTargetType
import com.eladfinish.bible.contest.screens.mikud.model.BookUi
import com.eladfinish.bible.contest.screens.mikud.model.Selection
import com.eladfinish.bible.contest.screens.mikud.usecases.BuildBooksListUseCase
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import com.eladfinish.bible.contest.ui.extensions.navigationBarContentPadding
import com.eladfinish.bible.contest.utils.HebrewWithBackgroundPreview
import dev.icerock.moko.resources.compose.colorResource
import dev.icerock.moko.resources.compose.stringResource

@Composable
internal fun BookList(
    modifier: Modifier = Modifier,
    mikudState: List<BookState>,
    onClick: (BookClickData) -> Unit = {},
) {
    Column {
        Text(
            text = stringResource(resource = SharedRes.strings.book_name),
            modifier = modifier.padding(horizontal = 6.dp),
            color = Color.Black,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Right,
            style = MaterialTheme.typography.bodySmall.copy(fontSize = 22.sp)
        )
        BookCheckboxList(
            books = getBooks(mikudState = mikudState),
            onClick = onClick,
        )
    }
}

private fun getBooks(mikudState: List<BookState>): List<BookUi> =
    mikudState.map {
        val count = it.includedChaptersCount
        BookUi(
            index = it.index,
            name = it.name,
            selection = when (count) {
                0 -> Selection.NONE
                it.chapters.size -> Selection.ALL
                else -> Selection.PARTIAL
            },
            totalChapters = it.chapters.size,
            selectedChapters = count,
        )
    }

@Composable
private fun BookCheckboxList(
    books: List<BookUi>,
    onClick: (BookClickData) -> Unit = {},
) {

    val state = rememberLazyListState()
    LazyColumn(
        state = state,
        modifier = Modifier.mouseVerticalScrollbar(state),
        contentPadding = navigationBarContentPadding(),
        verticalArrangement = Arrangement.spacedBy(0.dp),
    ) {
        items(
            items = books,
            key = { it.name }
        ) { book ->
            val index = book.index
            val name = book.name

            Row(
                modifier = Modifier.wrapContentHeight(),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Checkbox(
                    colors = CheckboxDefaults.colors(
                        checkedColor = colorResource(resource = SharedRes.colors.colorAccent),
                        uncheckedColor = Color.DarkGray,
                        checkmarkColor = Color.Black
                    ),
                    checked = book.isChecked,
                    modifier = Modifier
                        .align(alignment = Alignment.CenterVertically),
                    onCheckedChange = {
                        onClick.invoke(
                            BookClickData(
                                bookTargetType = BookTargetType.CHECKBOX,
                                index = index,
                                selected = it
                            )
                        )
                    },
                )
                Row(
                    modifier = Modifier
                        .align(alignment = Alignment.CenterVertically)
                        .padding(top = 4.dp)
                        .clickable(
                            onClick = {
                                onClick.invoke(
                                    BookClickData(
                                        bookTargetType = BookTargetType.LABEL,
                                        index = index,
                                        selected = book.isChecked
                                    )
                                )
                            },
                        )
                ) {
                    Text(
                        text = name,
                        modifier = Modifier
                            .align(alignment = Alignment.Bottom),
                        color = Color.Black,
                        fontSize = 17.sp,
                        fontWeight = FontWeight.Bold,
                        textAlign = TextAlign.Right,
                        style = MaterialTheme.typography.bodySmall.copy(
                            textDecoration = when (book.selection) {
                                Selection.NONE -> TextDecoration.LineThrough
                                Selection.PARTIAL -> TextDecoration.None
                                Selection.ALL -> TextDecoration.Underline
                            }
                        )
                    )
                    Text(
                        text = "(${book.selectedChapters}/${book.totalChapters})",
                        modifier = Modifier
                            .padding(4.dp)
                            .align(alignment = Alignment.Bottom),
                        color = Color.Black,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Bold,
                        textAlign = TextAlign.Right,
                        style = MaterialTheme.typography.bodySmall,
                    )
                }
            }
        }
    }
}

@HebrewWithBackgroundPreview
@Composable
private fun BookListPreview() {
    BookList(mikudState = BuildBooksListUseCase().invoke(full = true))
}
